import { createElement, useCallback, useEffect, useRef, useState } from 'react';
import { SearchProviderCatalogSchema } from "./search-routing-remote.js";
import { normalizeWebSearchRouting } from "./search-routing-policy.js";
import { nativeOptionStyle, nativeSelectStyle } from "./native-select-style.js";
import { nativeCardStyle, nativeButtonStyle, nativeHeadingStyle, nativeCaptionStyle, nativeSettingsCss } from "./native-settings-style.js";
const GITHUB_COPILOT_SETTINGS_NAMESPACE = 'github-copilot';
const WEB_SEARCH_ROUTING_SETTINGS_NAMESPACE = 'github-copilot-search-routing';
const GITHUB_COPILOT_SEARCH_PROVIDER = 'github-copilot-hosted';
const DEEPSEEK_SEARCH_PROVIDER = 'deepseek-official';
const cardStyle = {
    ...nativeCardStyle, display: 'grid', gap: '12px', marginTop: '16px',
};
const restartBannerStyle = {
    padding: '12px 14px', borderRadius: 'var(--dsw-radius-md, 12px)', fontSize: '12px', lineHeight: '18px',
    border: '1px solid color-mix(in srgb, var(--dsw-alias-color-warning, #e3a300) 40%, transparent)',
    background: 'color-mix(in srgb, var(--dsw-alias-color-warning, #e3a300) 12%, transparent)',
    display: 'grid', gap: '4px',
};
const lifecycleNoticeStyle = {
    ...restartBannerStyle,
    borderColor: 'color-mix(in srgb, currentColor 22%, transparent)',
    background: 'color-mix(in srgb, currentColor 6%, transparent)',
};
const fieldStyle = { display: 'grid', gap: '6px', minWidth: 0 };
const optionsRowStyle = { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' };
const buttonStyle = nativeButtonStyle;
const hintStyle = { opacity: 0.8, fontSize: '12px', lineHeight: 1.5, overflowWrap: 'anywhere', margin: 0 };
function record(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {};
}
function revisionOf(value) {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined;
}
function providerLabel(id) {
    return id === GITHUB_COPILOT_SEARCH_PROVIDER ? 'GitHub Copilot' : id === DEEPSEEK_SEARCH_PROVIDER ? 'DeepSeek' : id;
}
function saveFailure(error) {
    const code = record(error).code;
    if (code === 'settings-conflict')
        return 'Search settings changed since you opened this page. Reload settings, then review and save your choices again.';
    if (code === 'settings-rejected')
        return 'The settings service rejected this change. Reload settings to check whether editing is available, then try again.';
    return 'Could not confirm the save. Reload settings to check saved values before retrying. Your current choices have been kept.';
}
const searchDefaults = {
    enabled: true, providers: [], routeWebSearch: true, includeSources: true,
    stripServerTools: true, idleTimeoutMs: 300_000, probe: true,
    probeTimeoutMs: 30_000, searchFallback: 'deepseek',
};
const timeoutLimit = 2_147_483_647;
function searchOptions(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return undefined;
    const source = record(value);
    const options = { ...searchDefaults, ...source };
    if (typeof options.enabled !== 'boolean' || typeof options.routeWebSearch !== 'boolean'
        || typeof options.includeSources !== 'boolean' || typeof options.stripServerTools !== 'boolean'
        || typeof options.probe !== 'boolean' || !Array.isArray(options.providers)
        || options.providers.some((id) => typeof id !== 'string')
        || options.searchFallback !== 'deepseek' && options.searchFallback !== 'none'
        || ![options.idleTimeoutMs, options.probeTimeoutMs].every(number => typeof number === 'number'
            && Number.isSafeInteger(number) && number >= 1 && number <= timeoutLimit))
        return undefined;
    return {
        enabled: options.enabled, routeWebSearch: options.routeWebSearch,
        includeSources: options.includeSources, stripServerTools: options.stripServerTools,
        probe: options.probe, searchFallback: options.searchFallback,
        providers: options.providers.join(', '),
        idleTimeoutMs: String(options.idleTimeoutMs), probeTimeoutMs: String(options.probeTimeoutMs),
    };
}
/** Edit only explicit Copilot hosted-search leaves; provider routing remains a separate CAS. */
export function HostedSearchSettingsCard({ settings }) {
    const [draft, setDraft] = useState(() => searchOptions({}));
    const [saved, setSaved] = useState();
    const [revision, setRevision] = useState();
    const [writable, setWritable] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const lifecycle = useRef({ active: false, generation: 0, busy: false });
    const load = useCallback(async () => {
        const owner = lifecycle.current;
        if (!owner.active || owner.busy)
            return;
        const generation = ++owner.generation;
        owner.busy = true;
        setLoading(true);
        setWritable(false);
        setMessage('');
        try {
            const result = await settings.describe();
            if (!owner.active || generation !== owner.generation)
                return;
            if (!result.ok) {
                setMessage('Could not load Copilot search options. Reload to retry.');
                return;
            }
            const entry = result.value.namespaces.find(item => item.ns === GITHUB_COPILOT_SETTINGS_NAMESPACE);
            const next = entry && searchOptions(entry.value);
            const nextRevision = revisionOf(entry?.revision);
            if (!next || nextRevision === undefined) {
                setMessage('Copilot search settings are unavailable or invalid. Review the saved configuration.');
                return;
            }
            setDraft(next);
            setSaved(next);
            setRevision(nextRevision);
            setWritable(result.value.writable === true);
            if (!result.value.writable)
                setMessage('Copilot search settings are read-only in this deployment.');
        }
        catch {
            if (owner.active && generation === owner.generation)
                setMessage('Could not load Copilot search options. Reload to retry.');
        }
        finally {
            if (owner.active && generation === owner.generation) {
                owner.busy = false;
                setLoading(false);
            }
        }
    }, [settings]);
    useEffect(() => {
        const owner = lifecycle.current;
        owner.active = true;
        void load();
        return () => { owner.active = false; owner.generation++; owner.busy = false; };
    }, [load]);
    const update = (key, value) => {
        setDraft(current => ({ ...current, [key]: value }));
        setMessage('');
    };
    const disabled = loading || saving || !writable || revision === undefined || saved === undefined;
    const save = async () => {
        const owner = lifecycle.current;
        if (disabled || owner.busy || saved === undefined || revision === undefined)
            return;
        const ops = [];
        for (const key of ['enabled', 'routeWebSearch', 'includeSources', 'stripServerTools', 'probe', 'searchFallback']) {
            if (draft[key] !== saved[key])
                ops.push({ op: 'set', path: [key], value: draft[key] });
        }
        for (const key of ['idleTimeoutMs', 'probeTimeoutMs']) {
            if (draft[key] === saved[key])
                continue;
            const value = Number(draft[key]);
            if (!/^\d+$/.test(draft[key]) || !Number.isSafeInteger(value) || value < 1 || value > timeoutLimit) {
                setMessage(`${key} must be a whole number between 1 and ${timeoutLimit}.`);
                return;
            }
            ops.push({ op: 'set', path: [key], value });
        }
        if (draft.providers !== saved.providers) {
            const providers = draft.providers.split(',').map(id => id.trim());
            if (providers.some(id => !id) && draft.providers.trim() !== '' || new Set(providers).size !== providers.length) {
                setMessage('Enter distinct route IDs separated by commas, or leave the allowlist empty.');
                return;
            }
            ops.push({ op: 'set', path: ['providers'], value: draft.providers.trim() === '' ? [] : providers });
        }
        if (ops.length === 0) {
            setMessage('No search options changed.');
            return;
        }
        const generation = owner.generation;
        owner.busy = true;
        setSaving(true);
        setMessage('');
        try {
            const result = await settings.mutate(GITHUB_COPILOT_SETTINGS_NAMESPACE, ops, revision);
            if (!owner.active || generation !== owner.generation)
                return;
            if (!result.ok) {
                setMessage(saveFailure(result.error));
                return;
            }
            const nextRevision = revisionOf(result.value.revision);
            if (nextRevision === undefined) {
                setRevision(undefined);
                setMessage('Save returned no revision. Reload settings before editing again.');
                return;
            }
            setRevision(nextRevision);
            setSaved(draft);
            setMessage('Saved Copilot search options. Availability is checked when you search.');
        }
        catch (error) {
            if (owner.active && generation === owner.generation)
                setMessage(saveFailure(error));
        }
        finally {
            if (owner.active && generation === owner.generation) {
                owner.busy = false;
                setSaving(false);
            }
        }
    };
    const toggles = [
        ['enabled', 'Enable Copilot hosted search'],
        ['routeWebSearch', 'Route web search through this plugin'],
        ['includeSources', 'Include citations in inline requests'],
        ['stripServerTools', 'Remove local variants of hosted-search tools'],
        ['probe', 'Verify native search capability before use'],
    ];
    return createElement('details', { 'data-dsh-copilot-search-options': true, 'data-copilot-native-ui': true, style: cardStyle }, createElement('summary', null, 'Copilot hosted-search options'), createElement('style', null, nativeSettingsCss), createElement('p', { style: hintStyle }, 'These settings affect Copilot only. Disabling capability verification explicitly trusts the provider protocol. The allowlist restricts routes; it does not choose a search model.'), ...toggles.map(([key, label]) => createElement('label', { key, style: optionsRowStyle }, createElement('input', { type: 'checkbox', disabled, checked: draft[key], 'data-dsh-copilot-search-option': key,
        onChange: (event) => update(key, event.currentTarget.checked) }), label)), createElement('label', { style: fieldStyle }, 'Allowed route IDs (comma-separated; empty follows the initiating route)', createElement('input', { type: 'text', disabled, value: draft.providers, 'data-dsh-copilot-search-option': 'providers',
        onChange: (event) => update('providers', event.currentTarget.value) })), ...['idleTimeoutMs', 'probeTimeoutMs'].map(key => createElement('label', { key, style: fieldStyle }, key === 'idleTimeoutMs' ? 'Search idle timeout (ms)' : 'Capability probe timeout (ms)', createElement('input', { type: 'number', min: 1, max: timeoutLimit, step: 1, disabled, value: draft[key],
        'data-dsh-copilot-search-option': key,
        onChange: (event) => update(key, event.currentTarget.value) }))), createElement('label', { style: fieldStyle }, 'Legacy failure fallback', createElement('select', { disabled, value: draft.searchFallback, style: nativeSelectStyle(disabled),
        'data-dsh-copilot-search-option': 'searchFallback',
        onChange: (event) => update('searchFallback', event.currentTarget.value === 'none' ? 'none' : 'deepseek') }, createElement('option', { value: 'deepseek', style: nativeOptionStyle() }, 'Use configured DeepSeek fallback'), createElement('option', { value: 'none', style: nativeOptionStyle() }, 'No legacy failure fallback'))), createElement('div', { style: optionsRowStyle }, createElement('button', { type: 'button', style: buttonStyle, disabled, 'data-dsh-copilot-search-options-save': true,
        onClick: () => { void save(); } }, saving ? 'Saving…' : 'Save Copilot search options'), createElement('button', { type: 'button', style: buttonStyle, disabled: loading || saving,
        onClick: () => { void load(); } }, 'Reload options')), createElement('p', { role: 'status', 'aria-live': 'polite', style: hintStyle }, message));
}
/** Save provider routing independently of account discovery and provider-specific model settings. */
export function WebSearchRoutingCard(props) {
    const [draft, setDraft] = useState({
        primary: 'auto', provider: DEEPSEEK_SEARCH_PROVIDER, legacy: true,
    });
    const [override, setOverride] = useState({ model: '' });
    const [providers, setProviders] = useState([]);
    const [catalogReady, setCatalogReady] = useState(false);
    const [restartRequired, setRestartRequired] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState();
    const [writable, setWritable] = useState(false);
    const [message, setMessage] = useState('');
    const lifecycle = useRef({ active: false, generation: 0, busy: false });
    const load = useCallback(async () => {
        const owner = lifecycle.current;
        if (!owner.active || owner.busy)
            return;
        const generation = ++owner.generation;
        const current = () => owner.active && owner.generation === generation;
        owner.busy = true;
        setLoading(true);
        setSaving(undefined);
        setWritable(false);
        setCatalogReady(false);
        setRestartRequired(false);
        setMessage('');
        setProviders([]);
        try {
            const [result, catalog] = await Promise.all([
                props.settings.describe(),
                Promise.resolve().then(() => props.routing.providers()).catch(() => undefined),
            ]);
            if (!current())
                return;
            if (!result.ok) {
                setMessage('Could not load search settings. Reload settings to retry.');
                return;
            }
            const copilot = result.value.namespaces.find(entry => entry.ns === GITHUB_COPILOT_SETTINGS_NAMESPACE);
            const legacyRouting = result.value.namespaces.find(entry => entry.ns === WEB_SEARCH_ROUTING_SETTINGS_NAMESPACE);
            const copilotValue = record(copilot?.value);
            const copilotRoutingValue = record(copilotValue.searchRouting);
            const legacyRoutingValue = record(legacyRouting?.value);
            const routingValue = Object.keys(copilotRoutingValue).length > 0 ? copilotRoutingValue : legacyRoutingValue;
            const normalized = normalizeWebSearchRouting({
                searchProvider: typeof routingValue.searchProvider === 'string' ? routingValue.searchProvider : undefined,
                searchMode: routingValue.searchMode === 'fixed' ? 'fixed' : 'auto',
                defaultSearchProvider: typeof routingValue.defaultSearchProvider === 'string' ? routingValue.defaultSearchProvider : undefined,
            });
            const copilotRevision = revisionOf(copilot?.revision);
            setDraft({ primary: normalized.primaryProvider, provider: normalized.defaultProvider, legacy: normalized.legacy,
                routingRevision: copilotRevision });
            setOverride({ model: typeof copilotValue.searchModel === 'string' ? copilotValue.searchModel.trim() : '',
                revision: copilotRevision });
            const parsed = catalog?.ok ? SearchProviderCatalogSchema.safeParse(catalog.value) : undefined;
            const ids = parsed?.success ? parsed.data.providers.map(entry => entry.id) : [];
            if (!parsed?.success || !parsed.data.supported || new Set(ids).size !== ids.length || ids.includes('auto') || ids.includes('none')) {
                if (parsed?.success && !parsed.data.supported) {
                    setRestartRequired(true);
                    setMessage('Search service is not ready. Please restart Desktop to apply updates.');
                }
                else {
                    setMessage('Search provider list is unavailable. Reload after the routed search service is active.');
                }
                return;
            }
            setProviders(ids.toSorted());
            setCatalogReady(true);
            setWritable(result.value.writable === true);
            if (!copilot || copilotRevision === undefined) {
                setMessage('Web search routing settings are unavailable. Reload after the routing namespace is registered.');
            }
            else if (!result.value.writable) {
                setMessage('Search settings are read-only in this deployment.');
            }
        }
        catch {
            if (current())
                setMessage('Could not load search settings. Reload settings to retry.');
        }
        finally {
            if (current()) {
                owner.busy = false;
                setLoading(false);
            }
        }
    }, [props.settings, props.routing]);
    useEffect(() => {
        const owner = lifecycle.current;
        owner.active = true;
        void load();
        return () => { owner.active = false; owner.generation++; owner.busy = false; };
    }, [load]);
    const usesCopilot = draft.primary === GITHUB_COPILOT_SEARCH_PROVIDER || draft.provider === GITHUB_COPILOT_SEARCH_PROVIDER;
    useEffect(() => {
        const revision = props.settingsRevision;
        if (!revision)
            return;
        setDraft(value => value.routingRevision === revision.previous ? { ...value, routingRevision: revision.next } : value);
        setOverride(value => value.revision === revision.previous ? { ...value, revision: revision.next } : value);
    }, [props.settingsRevision]);
    const disabled = loading || saving !== undefined || !writable || draft.routingRevision === undefined || !catalogReady;
    const primaryKnown = draft.primary === 'auto' || draft.primary === 'none' || providers.includes(draft.primary);
    const fallbackKnown = draft.provider === 'none' || providers.includes(draft.provider);
    const missingProvider = !primaryKnown || !fallbackKnown;
    const save = useCallback(async () => {
        const owner = lifecycle.current;
        if (!owner.active || owner.busy || disabled)
            return;
        if (missingProvider) {
            setMessage('Choose a registered search provider, or disable the fallback with None.');
            return;
        }
        const generation = owner.generation;
        const current = () => owner.active && owner.generation === generation;
        const provider = draft.provider, primary = draft.primary;
        owner.busy = true;
        setSaving('routing');
        setMessage('');
        try {
            const result = await props.settings.mutate(GITHUB_COPILOT_SETTINGS_NAMESPACE, [
                { op: 'set', path: ['searchRouting', 'searchProvider'], value: primary },
                { op: 'set', path: ['searchRouting', 'defaultSearchProvider'], value: provider },
            ], draft.routingRevision);
            if (!current())
                return;
            if (!result.ok) {
                setMessage(saveFailure(result.error));
                return;
            }
            const revision = revisionOf(result.value.revision);
            if (revision === undefined) {
                setDraft(value => current() ? { ...value, routingRevision: undefined } : value);
                setMessage('Save returned no revision. Reload settings before editing again.');
                return;
            }
            setDraft(value => current() ? { ...value, primary, provider, routingRevision: revision, legacy: false } : value);
            setOverride(value => current() ? { ...value, revision } : value);
            setMessage('Saved. New searches use this routing. Search availability is checked when you search.');
        }
        catch (error) {
            if (current())
                setMessage(saveFailure(error));
        }
        finally {
            if (current()) {
                owner.busy = false;
                setSaving(undefined);
            }
        }
    }, [disabled, draft, missingProvider, props.settings]);
    // Explicitly reset an old override without also saving the provider draft.
    // Empty overrides inherited composition values; unset would reveal them again.
    const clearOverride = useCallback(async () => {
        const owner = lifecycle.current;
        if (!owner.active || owner.busy || disabled || override.revision === undefined)
            return;
        const generation = owner.generation;
        const current = () => owner.active && owner.generation === generation;
        owner.busy = true;
        setSaving('override');
        setMessage('');
        try {
            const result = await props.settings.mutate(GITHUB_COPILOT_SETTINGS_NAMESPACE, [{ op: 'set', path: ['searchModel'], value: '' }], override.revision);
            if (!current())
                return;
            if (!result.ok) {
                setMessage(saveFailure(result.error));
                return;
            }
            const revision = revisionOf(result.value.revision);
            if (revision === undefined) {
                setOverride(value => current() ? { ...value, revision: undefined } : value);
                setMessage('Save returned no revision. Reload settings before editing again.');
                return;
            }
            setOverride({ model: '', revision });
            setDraft(value => current() ? { ...value, routingRevision: revision } : value);
            setMessage('Copilot model selection is now automatic. Unsaved provider choices have not been applied.');
        }
        catch (error) {
            if (current())
                setMessage(saveFailure(error));
        }
        finally {
            if (current()) {
                owner.busy = false;
                setSaving(undefined);
            }
        }
    }, [disabled, override, props.settings]);
    const providerOptions = () => providers.map(id => createElement('option', { key: id, value: id, style: nativeOptionStyle() }, providerLabel(id)));
    const unavailableOption = (id, known) => known ? null : createElement('option', { value: id, disabled: true, style: nativeOptionStyle(true) }, `${id || '(empty)'} — unavailable`);
    return createElement('section', { style: cardStyle, 'data-dsh-web-search-routing': true,
        'data-copilot-native-ui': true, 'aria-busy': loading || saving !== undefined }, createElement('style', null, nativeSettingsCss), createElement('div', { role: 'note', style: lifecycleNoticeStyle, 'data-dsh-web-search-lifecycle-notice': true }, createElement('strong', { style: { fontWeight: 500 } }, 'Desktop 0.2.0-rc.2 lifecycle note / Desktop 0.2.0-rc.2 生命周期提示'), createElement('span', { style: { opacity: 0.85 } }, 'After disabling, removing, or upgrading this plugin, fully exit and restart Desktop to complete Web service recomposition. If the native manager shows only pending entries waiting for a service and this plugin is Off, restart instead of toggling it repeatedly. / 停用、移除或升级本插件后，请完全退出并重新启动 Desktop，以完成 Web 服务重编排。若原生管理器仅显示等待某项服务的 pending 条目且本插件已为 Off，请直接重启，不要反复切换。')), restartRequired ? createElement('div', { role: 'alert', style: restartBannerStyle, 'data-dsh-web-search-restart-banner': true }, createElement('strong', { style: { fontWeight: 500 } }, 'Restart required to complete update / 检测到更新，请重启 Desktop 完成生效'), createElement('span', { style: { opacity: 0.85 } }, 'Web search service composition requires a full application restart to rebind components. / Web 搜索服务组件更新需退出并重新启动 Desktop 以完成重编排。')) : null, createElement('div', null, createElement('h3', { style: nativeHeadingStyle }, 'Web search'), createElement('p', { style: { margin: '4px 0 0', color: 'var(--dsw-alias-label-secondary, GrayText)', fontSize: '14px', lineHeight: '22px' } }, 'Choose who runs web searches. This does not change your Chat model.')), createElement('label', { style: fieldStyle }, createElement('span', { style: { ...nativeCaptionStyle, fontWeight: 500 } }, 'Search provider'), createElement('select', { style: nativeSelectStyle(disabled), disabled, value: draft.primary, 'data-dsh-web-search-mode': true,
        onChange: (event) => { const primary = event.currentTarget.value; setMessage(''); setDraft(current => ({ ...current, primary })); } }, createElement('option', { value: 'auto', style: nativeOptionStyle() }, 'Auto — follow Chat'), draft.primary === 'none' ? createElement('option', { value: 'none', style: nativeOptionStyle() }, 'Disabled — saved configuration') : null, ...providerOptions(), unavailableOption(draft.primary, primaryKnown)), createElement('span', { style: hintStyle }, 'Auto follows the Chat provider when it supports search. A selected provider stays fixed when you switch Chat models.')), createElement('label', { style: fieldStyle }, createElement('span', { style: { ...nativeCaptionStyle, fontWeight: 500 } }, 'Fallback provider'), createElement('select', { style: nativeSelectStyle(disabled), disabled, value: draft.provider, 'data-dsh-web-search-provider': true,
        onChange: (event) => { const provider = event.currentTarget.value; setMessage(''); setDraft(current => ({ ...current, provider })); } }, createElement('option', { value: 'none', style: nativeOptionStyle() }, 'None — no fallback'), ...providerOptions(), unavailableOption(draft.provider, fallbackKnown)), createElement('span', { style: hintStyle }, 'Used if Auto has no matching provider or the primary search fails. May incur the fallback provider’s charges. The same provider is not tried twice.')), catalogReady && providers.length === 0 ? createElement('p', { role: 'status', style: hintStyle }, 'No search providers are registered. Enable a search provider, then reload settings.') : null, catalogReady && missingProvider ? createElement('p', { role: 'status', style: hintStyle }, 'A saved provider is no longer registered. Choose an available entry; it has not been replaced automatically.') : null, draft.legacy && catalogReady ? createElement('p', { style: hintStyle }, 'Saving adopts the selected fallback for new searches. Cancellation and account-proof invalidation never trigger fallback.') : null, usesCopilot ? createElement('p', { style: hintStyle }, 'Copilot handles search model selection. No model setup is needed here. A signed-in account and a model with verified search support are required when searching.') : null, usesCopilot && override.model ? createElement('details', { 'data-dsh-copilot-search-override': true }, createElement('summary', null, 'Existing Copilot model override'), createElement('div', { style: { ...fieldStyle, marginTop: '8px' } }, createElement('p', { style: hintStyle }, 'Your saved override ', createElement('code', null, override.model), ' is preserved. Saving providers does not change it.'), createElement('button', { type: 'button', style: buttonStyle, disabled: disabled || override.revision === undefined,
        'data-dsh-copilot-search-reset': true, onClick: () => { void clearOverride(); } }, saving === 'override' ? 'Updating…' : 'Use automatic model selection'))) : null, createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' } }, createElement('button', { type: 'button', style: buttonStyle, disabled: disabled || missingProvider,
        'data-dsh-web-search-save': true, onClick: () => { void save(); } }, loading ? 'Loading…' : saving === 'routing' ? 'Saving…' : 'Save search routing'), createElement('button', { type: 'button', style: buttonStyle, disabled: loading || saving !== undefined,
        title: 'Reload saved values and discard unsaved edits', 'data-dsh-web-search-reload': true, onClick: () => { void load(); } }, 'Reload settings')), createElement('p', { role: 'status', 'aria-live': 'polite', 'aria-atomic': true, style: { ...hintStyle, ...message ? {} : { display: 'none' } } }, message));
}
//# sourceMappingURL=web-search-routing-card.js.map